/**
 * ТЗ 0092 — участники Zoom по занятиям группы: чистые помощники.
 *
 * Коды с сервера — строки в нижнем регистре; подписи и тона — здесь. Ключи
 * записаны целиком (см. шапку `participation.ts`).
 */
import { todayInWarsaw, toApiDate } from './zoomTime';

import type { ParticipationTone } from './participation';

export type ZoomParticipantsTone = ParticipationTone;

/** Сколько дней назад начинается период карточки по умолчанию. */
export const ZOOM_PARTICIPANTS_DEFAULT_DAYS = 30;

/** Последние 30 дней по Варшаве, включая сегодня. */
export const defaultZoomParticipantsPeriod = (): {
  dateFrom: string;
  dateTo: string;
} => {
  const today = todayInWarsaw();
  return {
    dateFrom: toApiDate(today.minus({ days: ZOOM_PARTICIPANTS_DEFAULT_DAYS })),
    dateTo: toApiDate(today),
  };
};

/**
 * Статус покрытия занятия (`ZoomLessonCoverageType.status`). Коды загрузки
 * проведения (`participantsStatus`: pending|ok|empty|expired|forbidden|error)
 * — подмножество тех же кодов и подписываются теми же словами.
 */
export const ZOOM_COVERAGE_STATUS_KEYS: Record<string, string> = {
  future: 'pages.groupDetail.zoomParticipants.status.future',
  ok: 'pages.groupDetail.zoomParticipants.status.ok',
  pending: 'pages.groupDetail.zoomParticipants.status.pending',
  error: 'pages.groupDetail.zoomParticipants.status.error',
  forbidden: 'pages.groupDetail.zoomParticipants.status.forbidden',
  expired: 'pages.groupDetail.zoomParticipants.status.expired',
  empty: 'pages.groupDetail.zoomParticipants.status.empty',
  ambiguous: 'pages.groupDetail.zoomParticipants.status.ambiguous',
  offline: 'pages.groupDetail.zoomParticipants.status.offline',
  missing: 'pages.groupDetail.zoomParticipants.status.missing',
  no_source: 'pages.groupDetail.zoomParticipants.status.no_source',
  not_fetched: 'pages.groupDetail.zoomParticipants.status.not_fetched',
};
export const ZOOM_COVERAGE_STATUS_UNKNOWN_KEY =
  'pages.groupDetail.zoomParticipants.status.unknown';

export const zoomCoverageStatusKey = (code?: string | null): string =>
  (code && ZOOM_COVERAGE_STATUS_KEYS[code]) || ZOOM_COVERAGE_STATUS_UNKNOWN_KEY;

const COVERAGE_TONES: Record<string, ZoomParticipantsTone> = {
  future: 'neutral',
  ok: 'ok',
  pending: 'info',
  error: 'danger',
  forbidden: 'danger',
  expired: 'warn',
  empty: 'warn',
  ambiguous: 'warn',
  offline: 'neutral',
  missing: 'danger',
  no_source: 'warn',
  not_fetched: 'info',
};

export const zoomCoverageStatusTone = (
  code?: string | null,
): ZoomParticipantsTone => (code && COVERAGE_TONES[code]) || 'neutral';

/** Занятие ждёт решения оператора: какое проведение Zoom к нему относится. */
export const needsInstanceLink = (status?: string | null): boolean =>
  status === 'ambiguous' || status === 'missing';

/** Как проведение нашлось для занятия (`rule` / `lessonRule`). */
export const ZOOM_LESSON_RULE_KEYS: Record<string, string> = {
  manual: 'pages.groupDetail.zoomParticipants.lessonRule.manual',
  recording: 'pages.groupDetail.zoomParticipants.lessonRule.recording',
  join_url: 'pages.groupDetail.zoomParticipants.lessonRule.join_url',
  host_time: 'pages.groupDetail.zoomParticipants.lessonRule.host_time',
};

export const zoomLessonRuleKey = (code?: string | null): string | null =>
  (code && ZOOM_LESSON_RULE_KEYS[code]) || null;

/** Сопоставление участника с человеком (`matchStatus`). */
export const ZOOM_MATCH_STATUS_KEYS: Record<string, string> = {
  unmatched: 'pages.groupDetail.zoomParticipants.matchStatus.unmatched',
  auto: 'pages.groupDetail.zoomParticipants.matchStatus.auto',
  suggested: 'pages.groupDetail.zoomParticipants.matchStatus.suggested',
  manual: 'pages.groupDetail.zoomParticipants.matchStatus.manual',
  staff: 'pages.groupDetail.zoomParticipants.matchStatus.staff',
  ignored: 'pages.groupDetail.zoomParticipants.matchStatus.ignored',
  ambiguous: 'pages.groupDetail.zoomParticipants.matchStatus.ambiguous',
};

export const zoomMatchStatusKey = (code?: string | null): string =>
  (code && ZOOM_MATCH_STATUS_KEYS[code]) || ZOOM_MATCH_STATUS_KEYS.unmatched;

const MATCH_TONES: Record<string, ZoomParticipantsTone> = {
  unmatched: 'danger',
  auto: 'ok',
  suggested: 'gold',
  manual: 'info',
  staff: 'neutral',
  ignored: 'neutral',
  ambiguous: 'warn',
};

export const zoomMatchStatusTone = (
  code?: string | null,
): ZoomParticipantsTone => (code && MATCH_TONES[code]) || 'neutral';

/** По какому правилу сопоставлено (`matchRule`). */
export const ZOOM_MATCH_RULE_KEYS: Record<string, string> = {
  email: 'pages.groupDetail.zoomParticipants.matchRule.email',
  alias: 'pages.groupDetail.zoomParticipants.matchRule.alias',
  name: 'pages.groupDetail.zoomParticipants.matchRule.name',
  click: 'pages.groupDetail.zoomParticipants.matchRule.click',
  manual: 'pages.groupDetail.zoomParticipants.matchRule.manual',
  host: 'pages.groupDetail.zoomParticipants.matchRule.host',
  teacher: 'pages.groupDetail.zoomParticipants.matchRule.teacher',
};

export const zoomMatchRuleKey = (code?: string | null): string | null =>
  (code && ZOOM_MATCH_RULE_KEYS[code]) || null;

/** Решение оператора уже стоит — есть что сбрасывать. */
export const hasOperatorDecision = (matchStatus?: string | null): boolean =>
  matchStatus === 'manual' || matchStatus === 'ignored';

/** Коды отказов мутаций синка, разбора и привязки. */
export const ZOOM_PARTICIPANTS_ERROR_KEYS: Record<string, string> = {
  not_found: 'pages.groupDetail.zoomParticipants.errors.not_found',
  invalid_range: 'pages.groupDetail.zoomParticipants.errors.invalid_range',
  live_zoom_blocked:
    'pages.groupDetail.zoomParticipants.errors.live_zoom_blocked',
  already_running: 'pages.groupDetail.zoomParticipants.errors.already_running',
  instance_not_linked:
    'pages.groupDetail.zoomParticipants.errors.instance_not_linked',
  conflict: 'pages.groupDetail.zoomParticipants.errors.conflict',
  required: 'pages.groupDetail.zoomParticipants.errors.required',
  zoom_error: 'pages.groupDetail.zoomParticipants.errors.zoom_error',
  no_instances_in_window:
    'pages.groupDetail.zoomParticipants.errors.no_instances_in_window',
  not_linked: 'pages.groupDetail.zoomParticipants.errors.not_linked',
};
export const ZOOM_PARTICIPANTS_UNKNOWN_ERROR_KEY =
  'pages.groupDetail.zoomParticipants.errors.unknown';

export const zoomParticipantsErrorKey = (code?: string | null): string =>
  (code && ZOOM_PARTICIPANTS_ERROR_KEYS[code.toLowerCase()]) ||
  ZOOM_PARTICIPANTS_UNKNOWN_ERROR_KEY;

/** Секунды → целые минуты (как в отчёте: 59 секунд — это 1 минута). */
export const secondsToMinutes = (seconds?: number | null): number =>
  typeof seconds === 'number' && seconds > 0 ? Math.round(seconds / 60) : 0;

/** Только цифры из meeting id (`889 1234 5678` → `88912345678`). */
export const normalizeMeetingId = (value: string): string =>
  value.replace(/\D+/g, '');
